import { FirebaseError } from 'firebase/app';
import {
  GoogleAuthProvider,
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  onIdTokenChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  verifyPasswordResetCode,
  type User,
} from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../lib/api';
import { requestEmailVerification, requestPasswordReset } from '../queries/account';
import { firebaseConfigured } from '../lib/env';
import { auth } from '../lib/firebase';
import { forgetPersonalStorage } from '../lib/storage';
import { trackLogin, trackSignUp } from '../lib/analytics';
import { PASSWORD_MIN } from '../lib/password';
import { reset as resetAnalytics, track } from '../lib/amplitude';

/**
 * Firebase Auth, wrapped so the rest of the app never imports it directly.
 * Authorization is not decided here: the API resolves roles from Postgres on
 * every request. This only answers "who is holding this browser".
 */

export interface AuthContextValue {
  user: User | null;
  /** True until Firebase has restored (or ruled out) a persisted session. */
  loading: boolean;
  configured: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (name: string, email: string, password: string) => Promise<void>;
  /**
   * Ask Slate to mail a reset link. Falls back to Firebase's own email when
   * the API cannot mint the code (see queries/account.ts).
   */
  sendReset: (email: string) => Promise<void>;
  /** Whose account a reset link is for; throws if it is spent or expired. */
  verifyReset: (code: string) => Promise<string>;
  /** Save the new password and sign in with it, in one step. */
  completeReset: (code: string, password: string) => Promise<void>;
  /**
   * Mail the confirmation link for the signed-in account's address. Slate's
   * own email where the API can mint the code, Firebase's otherwise.
   */
  sendVerification: () => Promise<void>;
  /**
   * Redeem the code from that email. Also refreshes the ID token so the API
   * sees the address as proven on the very next request.
   */
  confirmEmail: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Firebase's error codes are not sentences. These are. */
export function authErrorMessage(error: unknown): string {
  const code = error instanceof FirebaseError ? error.code : '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That does not look like an email address.';
    case 'auth/missing-password':
      return 'Enter your password.';
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return `Passwords need at least ${PASSWORD_MIN} characters, with a letter and a number.`;
    case 'auth/email-already-in-use':
      return 'There is already an account with that email. Sign in instead.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'That email and password do not match an account.';
    case 'auth/expired-action-code':
      return 'That reset link has expired. Ask for a new one.';
    case 'auth/invalid-action-code':
      return 'That reset link has already been used, or it was mistyped. Ask for a new one.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'The Google sign-in window closed before finishing.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the Google sign-in window. Allow popups for this site and try again.';
    case 'auth/network-request-failed':
      return 'Could not reach the sign-in service. Check your connection.';
    case 'auth/operation-not-allowed':
      return 'That sign-in method is not enabled for this project yet.';
    default:
      if (error instanceof Error && error.message) return error.message;
      return 'Sign in failed. Try again.';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(firebaseConfigured);

  const queryClient = useQueryClient();
  /**
   * Whose answers the cache holds. Undefined until the first callback, then
   * a uid or null.
   */
  const cachedFor = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!firebaseConfigured) return;
    // onIdTokenChanged rather than onAuthStateChanged: it also fires on token
    // refresh, which keeps `user.getIdToken()` cheap for the API client.
    return onIdTokenChanged(auth, (next) => {
      // Anonymous sessions belong to the guest app, never to this one; if one
      // somehow lands here it is not a signed-in Slate user.
      const uid = next && !next.isAnonymous ? next.uid : null;
      /*
        Nothing in the cache is keyed by who asked for it: `['me']`,
        `['videos', scope]` and `['orgs']` are the same keys for everybody,
        so a second person signing in at this browser would be handed the
        first one's library out of memory and shown it while their own
        request was still in flight. A signed-out tab keeps the client
        alive, so this outlives the session unless somebody reloads.

        Cleared here rather than in signOut so that no way out of an account
        can miss it: signing out, deleting the account, a revoked token, or
        signing straight in as somebody else. This fires on a plain token
        refresh too, which is the same person and keeps what it has.
      */
      if (cachedFor.current !== undefined && cachedFor.current !== uid) {
        queryClient.clear();
        forgetPersonalStorage();
      }
      cachedFor.current = uid;
      setUser(next && !next.isAnonymous ? next : null);
      setLoading(false);
    });
  }, [queryClient]);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const credential = await signInWithPopup(auth, provider);
    // A Google sign-in is also how most people sign up; only a first one is
    // the conversion.
    if (getAdditionalUserInfo(credential)?.isNewUser) {
      trackSignUp('google');
      track('Signed Up', { method: 'google' });
    } else {
      trackLogin('google');
      track('Signed In', { method: 'google' });
    }
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email.trim(), password);
    trackLogin('password');
    track('Signed In', { method: 'password' });
  }, []);

  const signUpWithEmail = useCallback(async (name: string, email: string, password: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
    trackSignUp('password');
    track('Signed Up', { method: 'password' });
    const displayName = name.trim();
    if (displayName) {
      await updateProfile(credential.user, { displayName });
      // The API reads `name` from the ID token, which only picks up the new
      // profile after a forced refresh.
      await credential.user.getIdToken(true);
      setUser(auth.currentUser);
    }
  }, []);

  const sendReset = useCallback(async (email: string) => {
    try {
      await requestPasswordReset(email.trim());
    } catch (error) {
      // Only the "this environment cannot mint a code" answer is worth
      // working around: Firebase's own email is unbranded and lands on a
      // Google-hosted form, but it beats being locked out of a local or
      // half-seeded environment. Anything else is a real failure.
      if (error instanceof ApiError && error.code === 'not_configured') {
        await sendPasswordResetEmail(auth, email.trim());
        return;
      }
      throw error;
    }
  }, []);

  const verifyReset = useCallback(async (code: string) => verifyPasswordResetCode(auth, code), []);

  const completeReset = useCallback(
    async (code: string, password: string) => {
      // Verified again rather than trusting what the page was shown: this is
      // the address the new password is about to be saved against, and it is
      // the one we sign in as.
      const email = await verifyPasswordResetCode(auth, code);
      await confirmPasswordReset(auth, code, password);
      // The whole point of landing them here instead of back on /login.
      await signInWithEmail(email, password);
    },
    [signInWithEmail],
  );

  const sendVerification = useCallback(async () => {
    try {
      await requestEmailVerification();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'not_configured' && auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        return;
      }
      throw error;
    }
  }, []);

  const confirmEmail = useCallback(
    async (code: string) => {
      await applyActionCode(auth, code);
      // The claim lives in the token, and the token in hand still says
      // unverified. Reload the account and force a fresh token so both this
      // app and the API see the change now rather than within the hour.
      if (auth.currentUser) {
        await auth.currentUser.reload();
        await auth.currentUser.getIdToken(true);
        setUser(auth.currentUser);
        await queryClient.invalidateQueries({ queryKey: ['me'] });
      }
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    // Reported while we still know who is leaving, then the analytics
    // identity goes with the session.
    track('Signed Out');
    await firebaseSignOut(auth);
    resetAnalytics();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      sendReset,
      verifyReset,
      completeReset,
      sendVerification,
      confirmEmail,
      signOut,
    }),
    [
      completeReset,
      confirmEmail,
      loading,
      sendVerification,
      sendReset,
      signInWithEmail,
      signInWithGoogle,
      signOut,
      signUpWithEmail,
      user,
      verifyReset,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>.');
  return value;
}

/** Best available human name for the signed-in user. */
export function displayNameOf(user: User | null): string {
  if (!user) return '';
  return user.displayName?.trim() || user.email?.split('@')[0] || 'You';
}

/** "Garrett's Organization": the default name, from whoever is signing up. */
export function defaultOrganizationName(user: User | null): string {
  const first = displayNameOf(user).split(' ')[0];
  if (!first || first === 'You') return 'My Organization';
  return `${first}${first.endsWith('s') ? "'" : "'s"} Organization`;
}

